import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminExpenses, adminShareholders, type Expense, type ExpenseInput, type Shareholder } from '../../api/admin';
import { Plus, Pencil, Trash2, Download, MessageSquare } from 'lucide-react';

const EXPENSE_CATEGORIES = [
  'General', 'Marketing', 'Salaries', 'Rent', 'Utilities', 'Packaging', 'Shipping/Courier',
  'Software', 'Payment fees', 'Bank charges', 'Taxes/GST', 'Professional fees', 'Team outing', 'Other',
];
const PAYMENT_METHODS = ['UPI', 'Cash', 'Card', 'Bank transfer', 'Other'];
const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
];

const fmt = (n: number) => `₹${(Number.isFinite(n) ? n : 0).toLocaleString('en-IN')}`;
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const todayISO = () => new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);

function downloadCSV(filename: string, header: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

interface ExpenseForm {
  id: string | null;
  category: string;
  amount: string;
  expense_date: string;
  vendor: string;
  payment_method: string;
  payment_status: string;
  comment: string;
}

const emptyForm: ExpenseForm = {
  id: null, category: 'General', amount: '', expense_date: todayISO(),
  vendor: '', payment_method: 'UPI', payment_status: 'paid', comment: '',
};

export default function ExpensesTab({ isDark }: { isDark: boolean }) {
  const [rows, setRows] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [datePreset, setDatePreset] = useState<'all' | 'week' | 'month' | 'year' | 'custom'>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [modal, setModal] = useState<ExpenseForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [shareholders, setShareholders] = useState<Shareholder[]>([]);
  const [shareholderMode, setShareholderMode] = useState(false);
  const [selectedShareholderId, setSelectedShareholderId] = useState('');

  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const inp = `w-full px-3 py-2 rounded-lg border text-sm outline-none ${
    isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
           : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
  }`;

  const dateParams = useMemo(() => {
    const iso = (ms: number) => new Date(ms + IST_OFFSET_MS).toISOString().slice(0, 10);
    const now = Date.now();
    const DAY = 86400000;
    if (datePreset === 'week') return { from: iso(now - 7 * DAY) };
    if (datePreset === 'month') return { from: iso(now - 30 * DAY) };
    if (datePreset === 'year') return { from: iso(now - 365 * DAY) };
    if (datePreset === 'custom') {
      const p: { from?: string; to?: string } = {};
      if (customFrom) p.from = customFrom;
      if (customTo) p.to = customTo;
      return p;
    }
    return {} as { from?: string; to?: string };
  }, [datePreset, customFrom, customTo]);

  const load = useCallback(() => {
    setLoading(true);
    const params: { from?: string; to?: string; category?: string } = { ...dateParams };
    if (categoryFilter) params.category = categoryFilter;
    adminExpenses.list(Object.keys(params).length ? params : undefined)
      .then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, [dateParams, categoryFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { adminShareholders.list().then(setShareholders).catch(() => {}); }, []);

  const isUnpaid = (r: Expense) => r.payment_status === 'unpaid';
  const counts = useMemo<Record<string, number>>(() => ({
    all: rows.length,
    paid: rows.filter((r) => !isUnpaid(r)).length,
    unpaid: rows.filter((r) => isUnpaid(r)).length,
  }), [rows]);
  const visibleRows = useMemo(() => (
    statusFilter === 'all' ? rows : rows.filter((r) => (isUnpaid(r) ? 'unpaid' : 'paid') === statusFilter)
  ), [rows, statusFilter]);
  const total = useMemo(() => visibleRows.reduce((s, r) => s + r.amount, 0), [visibleRows]);

  const closeModal = () => {
    setModal(null);
    setShareholderMode(false);
    setSelectedShareholderId('');
    setError('');
  };

  const save = async () => {
    if (!modal) return;
    const amountNum = parseInt(modal.amount, 10);
    if (!modal.category || Number.isNaN(amountNum) || amountNum < 0) { setError('Enter a category and a valid whole-number amount (₹)'); return; }
    if (!modal.expense_date) { setError('Enter the expense date'); return; }
    if (!modal.id && shareholderMode && !selectedShareholderId) { setError('Select a shareholder for the investment entry'); return; }
    setSaving(true);
    setError('');
    const payload: ExpenseInput = {
      category: modal.category,
      amount: amountNum,
      expense_date: modal.expense_date || null,
      vendor: modal.vendor || null,
      payment_method: modal.payment_method || null,
      payment_status: modal.payment_status,
      comment: modal.comment || null,
    };
    try {
      if (modal.id) {
        await adminExpenses.update(modal.id, payload);
      } else if (shareholderMode && selectedShareholderId) {
        // Atomic double-entry: both expense and investment created in one DB transaction
        await adminExpenses.createWithInvestment({ ...payload, shareholder_id: selectedShareholderId });
      } else {
        await adminExpenses.create(payload);
      }
      closeModal();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this expense?')) return;
    try {
      await adminExpenses.delete(id);
      load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const exportExpenses = () => {
    const header = ['Date', 'Category', 'Paid to', 'Payment method', 'Status', 'Comment', 'Amount'];
    const csvRows = visibleRows.map((r) => [
      r.expense_date ? r.expense_date.slice(0, 10) : '', r.category, r.vendor ?? '',
      r.payment_method ?? '', r.payment_status, r.comment ?? '', r.amount,
    ]);
    downloadCSV('ZeroSmoke_Expenses.csv', header, csvRows);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => { setModal({ ...emptyForm }); setShareholderMode(false); setSelectedShareholderId(''); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg">
          <Plus size={16} /> Add Expense
        </button>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
          className={`px-3 py-2 rounded-lg border text-sm font-medium outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
          <option value="">All categories</option>
          {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={exportExpenses}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
          <Download size={15} /> Export CSV
        </button>
        <span className={`ml-auto text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total: <b>{fmt(total)}</b></span>
      </div>

      {/* Payment-status filter pills */}
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              statusFilter === s.value
                ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/20'
                : isDark ? 'border-gray-700 text-gray-400 hover:border-gray-500' : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            {s.label} ({counts[s.value] ?? 0})
          </button>
        ))}
      </div>

      {/* Expense-date filter */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Expense date:</span>
        {([['all', 'All time'], ['week', 'Last week'], ['month', 'Last month'], ['year', 'Last year'], ['custom', 'Custom']] as const).map(([v, label]) => (
          <button
            key={v}
            onClick={() => setDatePreset(v)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
              datePreset === v
                ? 'bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-500/20'
                : isDark ? 'border-gray-700 text-gray-400 hover:border-gray-500' : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
        {datePreset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className={`px-2 py-1 rounded-lg border text-xs ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200'}`} />
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>–</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className={`px-2 py-1 rounded-lg border text-xs ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200'}`} />
          </div>
        )}
      </div>

      <div className={`rounded-xl border overflow-x-auto ${card}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`text-left border-b ${isDark ? 'text-gray-400 border-gray-800' : 'text-gray-500 border-gray-200'}`}>
              <th className="font-medium py-3 px-4">Date</th>
              <th className="font-medium py-3 px-4">Category</th>
              <th className="font-medium py-3 px-4">Paid to</th>
              <th className="font-medium py-3 px-4">Payment</th>
              <th className="font-medium py-3 px-4">Status</th>
              <th className="font-medium py-3 px-4 text-right">Amount</th>
              <th className="font-medium py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="py-6 text-center text-gray-400">Loading…</td></tr>}
            {!loading && visibleRows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-gray-400">No expenses yet</td></tr>}
            {visibleRows.map((r) => (
              <tr key={r.id} className={`border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                <td className="py-3 px-4">
                  {r.expense_date ? new Date(r.expense_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '—'}
                </td>
                <td className="py-3 px-4"><span className="inline-block text-center min-w-[120px] px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-emerald-500/10 text-emerald-500">{r.category}</span></td>
                <td className="py-3 px-4">
                  <div className="max-w-[240px] break-words">{r.vendor || '—'}</div>
                  {r.comment && (
                    <p className={`text-[11px] italic mt-1 flex items-start gap-1 max-w-[240px] break-words ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <MessageSquare size={11} className="mt-0.5 shrink-0" /> {r.comment}
                    </p>
                  )}
                </td>
                <td className="py-3 px-4">{r.payment_method || '—'}</td>
                <td className="py-3 px-4">
                  <span className={`px-2.5 py-1 rounded-lg border text-xs font-semibold ${r.payment_status === 'unpaid' ? 'bg-red-500/10 text-red-500 border-red-500/30' : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'}`}>
                    {r.payment_status === 'unpaid' ? 'Unpaid' : 'Paid'}
                  </span>
                </td>
                <td className="py-3 px-4 text-right font-medium">{fmt(r.amount)}</td>
                <td className="py-3 px-4 text-right">
                  <button
                    onClick={() => { setModal({ id: r.id, category: r.category, amount: String(r.amount), expense_date: r.expense_date ? r.expense_date.slice(0, 10) : todayISO(), vendor: r.vendor || '', payment_method: r.payment_method || 'UPI', payment_status: r.payment_status || 'paid', comment: r.comment || '' }); setShareholderMode(false); setSelectedShareholderId(''); setError(''); }}
                    title="Edit"
                    className="text-gray-400 hover:text-emerald-500 mr-3"
                  ><Pencil size={15} /></button>
                  <button
                    onClick={() => remove(r.id)}
                    title="Delete"
                    className="text-gray-400 hover:text-red-500"
                  ><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-8 overflow-y-auto" onClick={closeModal}>
          <div className={`w-full max-w-md rounded-2xl border p-6 max-h-[90vh] overflow-y-auto ${card}`} onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">{modal.id ? 'Edit' : 'Add'} Expense</h3>
            {error && <div className="mb-3 text-sm text-red-500">{error}</div>}
            <div className="space-y-3">
              {/* Shareholder double-entry — only for new expenses */}
              {!modal.id && (
                <>
                  <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                    <input
                      type="checkbox"
                      id="shareholder-mode"
                      checked={shareholderMode}
                      onChange={(e) => { setShareholderMode(e.target.checked); if (!e.target.checked) setSelectedShareholderId(''); }}
                      className="w-4 h-4 accent-emerald-500 cursor-pointer"
                    />
                    <label htmlFor="shareholder-mode" className={`text-sm cursor-pointer select-none ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Shareholder expense — also create investment entry
                    </label>
                  </div>
                  {shareholderMode && (
                    <div>
                      <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Shareholder <span className="text-red-500">*</span></label>
                      <select value={selectedShareholderId} onChange={(e) => setSelectedShareholderId(e.target.value)} className={inp}>
                        <option value="">Select shareholder…</option>
                        {shareholders.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                      {selectedShareholderId && modal.category && (
                        <p className={`mt-1 text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Investment entry: <span className="font-medium">{modal.category}{modal.vendor ? ` - ${modal.vendor}` : ''}</span>
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Category <span className="text-red-500">*</span></label>
                <select value={modal.category} onChange={(e) => setModal({ ...modal, category: e.target.value })} className={inp}>
                  {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Amount (₹) <span className="text-red-500">*</span></label>
                  <input type="text" inputMode="numeric" min={0} step={1} value={modal.amount} onChange={(e) => setModal({ ...modal, amount: e.target.value.replace(/[^0-9]/g, '') })} className={inp} placeholder="0" />
                </div>
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date <span className="text-red-500">*</span></label>
                  <input type="date" value={modal.expense_date} onChange={(e) => setModal({ ...modal, expense_date: e.target.value })} className={inp} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Payment method</label>
                  <select value={modal.payment_method} onChange={(e) => setModal({ ...modal, payment_method: e.target.value })} className={inp}>
                    {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</label>
                  <select value={modal.payment_status} onChange={(e) => setModal({ ...modal, payment_status: e.target.value })} className={inp}>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Paid to (optional)</label>
                <input type="text" value={modal.vendor} onChange={(e) => setModal({ ...modal, vendor: e.target.value })} className={inp} />
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Comment (optional)</label>
                <textarea value={modal.comment} onChange={(e) => setModal({ ...modal, comment: e.target.value })} className={inp} rows={2} />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={save} disabled={saving} className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-lg text-sm">{saving ? 'Saving…' : 'Save'}</button>
              <button onClick={closeModal} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
